import { List } from "@dropbox/dig-components/list";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { createUxaElementId } from "@mirage/analytics/uxa";
import type { DigTooltipProps } from "@mirage/shared/util/DigTooltip";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import type { SVGProps } from "react";
import { forwardRef } from "react";
import type { NavLinkRenderProps } from "react-router-dom";
import { NavLink } from "react-router-dom";
import styles from "./GlobalNav.module.css";
import type { GlobalNavTab } from "./types";
import { getFeatureLine } from "./utils";

export function getGlobalNavLinkItemClassNames({
  isActive,
}: NavLinkRenderProps): string {
  if (isActive) return `${styles.globalNavLinkItem} ${styles.active}`;
  return styles.globalNavLinkItem;
}

type GlobalNavLinkProps = {
  label: string;
  to: string;
  state?: Record<string, boolean>;
  lineIcon: (props: SVGProps<SVGSVGElement>) => React.ReactElement;
  fillIcon: (props: SVGProps<SVGSVGElement>) => React.ReactElement;
  navTab: GlobalNavTab;
  showTooltip: boolean;
  iconBadge?: React.ReactElement;
  textBadge?: React.ReactElement;
  tooltip?: string;
  hasNotificationBadge?: boolean;
  onClick?: React.MouseEventHandler;
};

export const GlobalNavLink = forwardRef<HTMLAnchorElement, GlobalNavLinkProps>(
  (
    {
      label,
      to,
      state,
      lineIcon,
      fillIcon,
      navTab,
      showTooltip,
      iconBadge,
      textBadge,
      hasNotificationBadge,
      onClick,
      tooltip = label, // Default tooltip to label if not provided
    }: GlobalNavLinkProps,
    ref,
  ) => {
    return (
      <List.Item isSelectable className={styles.globalNavListItem}>
        <GlobalNavTooltip title={tooltip} disabled={!showTooltip}>
          <NavLink
            to={to}
            state={state}
            end
            ref={ref}
            className={getGlobalNavLinkItemClassNames}
            data-testid={`NavTab-${navTab}`}
            data-uxa-log={createUxaElementId(`${navTab}_nav_button`, {
              actionSurfaceComponent: "sidebar",
              featureLine: getFeatureLine(navTab),
            })}
            data-campaigns-element-id={`dash-sidebar-${navTab}-button`}
            onClick={onClick}
          >
            {({ isActive }) => (
              <>
                <List.Accessory>
                  <Box display="flex">
                    {hasNotificationBadge && iconBadge}
                    <UIIcon width={24} src={isActive ? fillIcon : lineIcon} />
                  </Box>
                </List.Accessory>
                <List.Content className={styles.globalNavLinkItemContent}>
                  <Text color="inherit">
                    {label}
                    {hasNotificationBadge && textBadge}
                  </Text>
                </List.Content>
              </>
            )}
          </NavLink>
        </GlobalNavTooltip>
      </List.Item>
    );
  },
);

GlobalNavLink.displayName = "GlobalNavLink";

export const GlobalNavTooltip = (props: DigTooltipProps) => (
  <DigTooltip placement="right" openDelay={500} isPortaled={true} {...props} />
);
